import { useCallback } from 'react';
import { createStore, useStore } from 'zustand';

import useFeatureOptions from '@core/hooks/useFeatureOptions';
import { useProjectStore, useSuperHubStore } from '@core/store';
import { routeSectionToNavLinkKey } from '@routes/SuperHub/types';

interface SectionHiddenNotificationData {
  sectionLabel: string;
  sectionLinkKey: string;
}

interface SectionHiddenNotificationState {
  data: SectionHiddenNotificationData | null;
}

/**
 * Tiny store that holds the section-hidden notification state. Rendered inline
 * by AdminEditorContainer instead of via react-hot-toast, which avoids issues
 * with the dual-Toaster setup (Hub + AdminEditorContainer) where toast.remove()
 * fails to reliably dismiss custom toasts.
 */
const sectionHiddenNotificationStore = createStore<SectionHiddenNotificationState>(() => ({
  data: null,
}));

export function useSectionHiddenNotificationData() {
  return useStore(sectionHiddenNotificationStore, s => s.data);
}

export function dismissSectionHiddenNotification() {
  sectionHiddenNotificationStore.setState({ data: null });
}

/**
 * Returns a function that shows a notification when a page is saved in a section
 * whose nav visibility is disabled. The notification offers to enable the section.
 *
 * The notification is rendered inline by AdminEditorContainer, which handles
 * auto-dismiss (5 seconds) and dismiss on section navigation.
 */
export default function useSectionHiddenNotification() {
  const routeSection = useSuperHubStore(s => s.routeSection);
  const sections = useProjectStore(s => s.data.appearance.navigation.links);
  const featureOptions = useFeatureOptions();

  return useCallback(
    (overrideSection?: string) => {
      const effectiveSection = overrideSection ?? routeSection;
      if (!effectiveSection) return;

      const linkKey = routeSectionToNavLinkKey[effectiveSection];
      if (!linkKey || !sections[linkKey]) return;

      const section = sections[linkKey];
      if (section.visibility === 'enabled') return;

      const labelKey =
        effectiveSection === 'docs' ? 'docs' : effectiveSection === 'page' ? 'custompages' : effectiveSection;
      const sectionLabel = section.alias || section.label || featureOptions[labelKey] || linkKey;

      sectionHiddenNotificationStore.setState({
        data: { sectionLabel, sectionLinkKey: linkKey },
      });
    },
    [routeSection, sections, featureOptions],
  );
}
